// Colour-name localisation for the pair picker.
//
// hezo-ops resolves each slot's colour and sends `{ key, name, hex }` (issue
// #715): `key` is the canonical, DE-canonical, spelling/case/language-folded
// colour key (`canonicalColorKey` in hezo-ops `src/lib/colors.ts`) — the
// single source of truth for colour identity. We keep the backend `hex` (it's
// correct) and localise the *display name* off `key` here, since the display
// language is a client toggle the backend can't know.
//
// The old mirrored normaliser + English→German alias map used to live here;
// they now live only in hezo-ops. If `key` is missing (a pre-#715 backend) or
// names a colour we have no translation for, we fall back to the raw name with
// a leading capital so something sensible still shows.

import { type Lang } from "./i18n";

// Localised display names per canonical colour key. Keys match hezo-ops'
// `canonicalColorKey` output (the German entries in its HEX_BY_KEY + the
// COLOR_KEY_ALIASES targets).
const COLOUR_NAMES: Record<string, Record<Lang, string>> = {
  weiss: { de: "Weiß", en: "White", es: "Blanco", it: "Bianco", ar: "أبيض" },
  schwarz: { de: "Schwarz", en: "Black", es: "Negro", it: "Nero", ar: "أسود" },
  tiefschwarz: { de: "Tiefschwarz", en: "Deep black", es: "Negro intenso", it: "Nero intenso", ar: "أسود عميق" },
  graphit: { de: "Graphit", en: "Graphite", es: "Grafito", it: "Grafite", ar: "غرافيت" },
  grau: { de: "Grau", en: "Grey", es: "Gris", it: "Grigio", ar: "رمادي" },
  hellgrau: { de: "Hellgrau", en: "Light grey", es: "Gris claro", it: "Grigio chiaro", ar: "رمادي فاتح" },
  beige: { de: "Beige", en: "Beige", es: "Beige", it: "Beige", ar: "بيج" },
  hellbeige: { de: "Hellbeige", en: "Light beige", es: "Beige claro", it: "Beige chiaro", ar: "بيج فاتح" },
  dunkelbraun: { de: "Dunkelbraun", en: "Dark brown", es: "Marrón oscuro", it: "Marrone scuro", ar: "بني داكن" },
  rot: { de: "Rot", en: "Red", es: "Rojo", it: "Rosso", ar: "أحمر" },
  korallrot: { de: "Korallenrot", en: "Coral red", es: "Rojo coral", it: "Rosso corallo", ar: "أحمر مرجاني" },
  pastellrosa: { de: "Pastellrosa", en: "Pastel pink", es: "Rosa pastel", it: "Rosa pastello", ar: "وردي باستيل" },
  lila: { de: "Lila", en: "Purple", es: "Morado", it: "Viola", ar: "أرجواني" },
  blau: { de: "Blau", en: "Blue", es: "Azul", it: "Blu", ar: "أزرق" },
  stahlblau: { de: "Stahlblau", en: "Steel blue", es: "Azul acero", it: "Blu acciaio", ar: "أزرق فولاذي" },
  ozeanblau: { de: "Ozeanblau", en: "Ocean blue", es: "Azul océano", it: "Blu oceano", ar: "أزرق محيطي" },
  mintgrun: { de: "Mintgrün", en: "Mint green", es: "Verde menta", it: "Verde menta", ar: "أخضر نعناعي" },
  salbeigrun: { de: "Salbeigrün", en: "Sage green", es: "Verde salvia", it: "Verde salvia", ar: "أخضر مريمي" },
  opalgrun: { de: "Opalgrün", en: "Opal green", es: "Verde ópalo", it: "Verde opale", ar: "أخضر أوبالي" },
  irischgrun: { de: "Irischgrün", en: "Irish green", es: "Verde irlandés", it: "Verde irlandese", ar: "أخضر إيرلندي" },
  pastellgelb: { de: "Pastellgelb", en: "Pastel yellow", es: "Amarillo pastel", it: "Giallo pastello", ar: "أصفر باستيل" },
};

/** Uppercase the first letter so an untranslated raw name still reads tidily. */
function capitalise(name: string): string {
  const s = name.trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * Localise a resolved slot colour to `lang`, keyed off the backend-supplied
 * canonical `key`. Falls back to a capitalised form of the raw `name` when the
 * key is missing (pre-#715 backend) or names a colour we don't translate yet.
 */
export function localiseColour(colour: { key?: string; name: string }, lang: Lang): string {
  const entry = colour.key ? COLOUR_NAMES[colour.key] : undefined;
  return entry ? entry[lang] : capitalise(colour.name);
}
